'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { RxCross1 } from 'react-icons/rx';

import { cn } from '@helpers/styleUtils';
import { ScrollArea } from './ScrollArea';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(function DialogOverlay({ className, ...props }, ref) {
    return (
        <DialogPrimitive.Overlay
            ref={ref}
            className={cn(
                'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                className,
            )}
            {...props}
        />
    );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

DialogOverlay.propTypes = {
    className: PropTypes.string,
};

const DialogContent = React.forwardRef(function DialogContent(
    { className, children, fullscreen = false, scrollEnabled = true,  ...props },
    ref,
) {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    'bg-white fixed left-[50%] top-[50%] z-50 grid w-11/12 max-h-[85vh] translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg',
                    className,
                    fullscreen && 'min-h-[calc(100dvh - 200px)]',
                )}
                {...props}
            >
                <div className="flex flex-col w-full overflow-hidden">
                    <div className="flex-shrink-0">
                        {children.find(child => child.type?.displayName === 'DialogHeader')}
                    </div>

                    {scrollEnabled ? (
                        <ScrollArea className="flex-1 mt-4 overflow-auto">
                            <div className="pr-4">
                                {children.filter(
                                    child => child.type?.displayName !== 'DialogHeader',
                                )}
                            </div>
                        </ScrollArea>
                    ) : (
                        <div className="mt-4">
                            {children.filter(child => child.type?.displayName !== 'DialogHeader')}
                        </div>
                    )}
                </div>
                <DialogPrimitive.Close className="bg-primary-blue text-white text-xl absolute -right-[15px] top-4 rounded-full ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none flex items-center justify-center w-8 h-8">
                    <RxCross1 />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            </DialogPrimitive.Content>
        </DialogPortal>
    );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

DialogContent.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node,
    scrollEnabled: PropTypes.bool,
    fullscreen: PropTypes.bool,
};

const DialogHeader = ({ className, ...props }) => (
    <div
        className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
        {...props}
    />
);
DialogHeader.displayName = 'DialogHeader';

DialogHeader.propTypes = {
    className: PropTypes.string,
};

const DialogFooter = ({ className, ...props }) => (
    <div
        className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
        {...props}
    />
);
DialogFooter.displayName = 'DialogFooter';

DialogFooter.propTypes = {
    className: PropTypes.string,
};

const DialogTitle = React.forwardRef(function DialogTitle({ className, ...props }, ref) {
    return (
        <DialogPrimitive.Title
            ref={ref}
            className={cn('text-lg font-semibold leading-none tracking-tight', className)}
            {...props}
        />
    );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

DialogTitle.propTypes = {
    className: PropTypes.string,
};

const DialogDescription = React.forwardRef(function DialogDescription(
    { className, ...props },
    ref,
) {
    return (
        <DialogPrimitive.Description
            ref={ref}
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

DialogDescription.propTypes = {
    className: PropTypes.string,
};

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogTrigger,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
